import { useMemo } from 'react'

import { InFilterDefinition } from '@factorialco/f0-react'
import { LegacyJobCatalogFeatureEnabledDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useJobCatalogRoleFilter } from 'modules/job_catalog/lib/filters/jobRole'

import { useAccessStatusFilter } from './accessStatus'
import { useEmployeesFilter } from './employees'
import { useLegalEntityFilter } from './legalEntity'
import { useTeamFilter } from './team'
import { useWorkplaceFilter } from './workplace'

type UseEmployeeFiltersOptions = {
  workplaceMode?: 'location' | 'locationAndWorkAreas'
}

export const useEmployeeFilters = ({
  workplaceMode = 'location',
}: UseEmployeeFiltersOptions = {}) => {
  const { company } = useCurrentSession()

  const { data: legacyJobCatalogFeatureEnabledData } = useQuery(
    LegacyJobCatalogFeatureEnabledDocument,
    {
      fetchPolicy: 'network-only',
      variables: {
        companyId: company.id,
      },
    }
  )

  const hasLegacyJobCatalog =
    !!legacyJobCatalogFeatureEnabledData?.features?.featuresConnection?.totalCount

  const hasJobCatalogTreeFeature = !hasLegacyJobCatalog

  const workplaceFilter = useWorkplaceFilter({
    withWorkAreas: workplaceMode === 'locationAndWorkAreas',
  })
  const workAreaFilter = useMemo(
    (): InFilterDefinition<string> => ({
      label: i18n.t('employees.employees_list.headers.workplace'),
      hideSelector: true,
      type: 'in',
      options: {
        options: [],
      },
    }),
    []
  )
  const teamFilter = useTeamFilter()
  const legalEntityFilter = useLegalEntityFilter()
  const jobCatalogRoleFilter = useJobCatalogRoleFilter({ hasJobCatalogTreeFeature })

  const reportingToFilter = useEmployeesFilter({
    label: i18n.t('employees.employees_list.filters.reporting_to'),
  })

  const timeoffManagerFilter = useEmployeesFilter({
    label: i18n.t('employees.employees_list.filters.timeoff_manager'),
  })

  const accessStatusFilter = useAccessStatusFilter()

  return useMemo(
    () => ({
      filters: {
        workplace: workplaceFilter,
        workArea: workAreaFilter,
        team: teamFilter,
        reportingTo: reportingToFilter,
        timeoffManager: timeoffManagerFilter,
        legalEntity: legalEntityFilter,
        jobRole: jobCatalogRoleFilter,
        accessStatus: accessStatusFilter,
      },
      hasJobCatalogTreeFeature,
    }),
    [
      workplaceFilter,
      workAreaFilter,
      teamFilter,
      reportingToFilter,
      timeoffManagerFilter,
      legalEntityFilter,
      jobCatalogRoleFilter,
      accessStatusFilter,
      hasJobCatalogTreeFeature,
    ]
  )
}

export type UseEmployeeFiltersResult = ReturnType<typeof useEmployeeFilters>
